import { MepaProcessEditor } from '../components/sync/MepaProcessEditor';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import toast from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type * as Monaco from 'monaco-editor';

import { usePermissions } from '../hooks/usePermissions';
import { syncAdminAPI } from '../utils/api';
import { SyncDaemonNode, SyncDispatchJob, SyncQueryDefinition, SyncTenantAccess } from '../types';

import { DriveProcessEditor } from '../components/sync/DriveProcessEditor';
import { ProcessSchedule } from '../components/sync/ProcessSchedule';
import type { SyncScheduleWindow } from '../types';
import { MAPPING_TARGET_CONFIG, type MappingTarget } from '../components/sync/mappingFields';

interface QueryFormState {
  query_id: string;
  name: string;
  description: string;
  sql_text: string;
  params_schema: string;
  business_key_fields: string;
  source_ts_field: string;
  schedule_minutes: number;
  schedule_window: SyncScheduleWindow;
  source_config: { node_id?: string };
  is_active: boolean;
}

interface TableColumnMeta {
  name: string;
  description?: string;
}

interface MappingColumnOption {
  value: string;
  label: string;
}

function parseFromTable(sql: string): { schema?: string; table?: string } {
  const match = sql.match(/\bfrom\s+([a-zA-Z0-9_]+)\.([a-zA-Z0-9_]+)/i);
  if (match) return { schema: match[1], table: match[2] };
  const noSchema = sql.match(/\bfrom\s+([a-zA-Z0-9_]+)/i);
  if (noSchema) return { table: noSchema[1] };
  return {};
}

function extractSelectColumns(sql: string): MappingColumnOption[] {
  const match = sql.match(/\bselect\b([\s\S]*?)\bfrom\b/i);
  if (!match) return [];
  const raw = match[1].trim();
  if (!raw || raw === '*') return [];
  const parts = raw.split(',').map((part) => part.trim()).filter(Boolean);
  const out: MappingColumnOption[] = [];
  for (const part of parts) {
    const asMatch = part.match(/^(.*)\s+as\s+([a-zA-Z0-9_]+)$/i);
    const sourceExpr = (asMatch ? asMatch[1] : part).trim();
    const alias = (asMatch ? asMatch[2] : '').trim();
    const cleanedSource = sourceExpr.replace(/["`[\]]/g, '').trim();
    const lastToken = (cleanedSource.split('.').pop() || cleanedSource).trim();
    const value = (alias || lastToken).trim();
    if (!value) continue;
    const label = alias ? `${cleanedSource} AS ${alias}` : cleanedSource;
    out.push({ value, label });
  }
  return out;
}

const emptyQueryForm: QueryFormState = {
  query_id: '',
  name: '',
  description: '',
  sql_text: '',
  params_schema: '{}',
  business_key_fields: '',
  source_ts_field: '',
  schedule_minutes: 60,
  schedule_window: {},
  source_config: {},
  is_active: true,
};

function normalizeIdentifier(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_');
}

function findBestColumnMatch(columns: string[], field: string): string {
  const normalizedField = normalizeIdentifier(field);
  if (!normalizedField) return '';
  return (
    columns.find((c) => normalizeIdentifier(c) === normalizedField) ||
    columns.find((c) => normalizeIdentifier(c).includes(normalizedField)) ||
    ''
  );
}

const SQL_KEYWORDS = [
  'SELECT',
  'FROM',
  'WHERE',
  'JOIN',
  'LEFT JOIN',
  'RIGHT JOIN',
  'INNER JOIN',
  'ORDER BY',
  'GROUP BY',
  'HAVING',
  'UNION',
  'AND',
  'OR',
  'NOT',
  'IN',
  'LIKE',
  'BETWEEN',
  'IS NULL',
  'IS NOT NULL',
  'DISTINCT',
  'AS',
  'CASE',
  'WHEN',
  'THEN',
  'ELSE',
  'END',
  'COUNT',
  'SUM',
  'AVG',
  'MIN',
  'MAX',
  'FETCH FIRST',
  'ROWS ONLY',
];

function clampRowsLimit(value: number): number {
  if (!Number.isFinite(value)) return 50;
  return Math.max(1, Math.min(100, Math.floor(value)));
}

function withRowsOnlyClause(sql: string, limit: number): string {
  const base = sql.trim().replace(/;+\s*$/, '');
  if (!base) return '';
  const normalizedLimit = clampRowsLimit(limit);
  const withoutExisting = base.replace(/\s+fetch\s+first\s+\d+\s+rows?\s+only\s*$/i, '');
  return `${withoutExisting}\nFETCH FIRST ${normalizedLimit} ROWS ONLY`;
}

export default function SyncQueryStudio() {
  const { hasPermission } = usePermissions();
  const canManageSync = hasPermission('sync:manage');
  const { queryId } = useParams<{ queryId: string }>();
  const isEdit = !!queryId;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const returnTab = searchParams.get('tab') || 'query-catalog';

  const [selectedTenantId, setSelectedTenantId] = useState(searchParams.get('tenant') || '');
  const [sourceType, setSourceType] = useState<'as400' | 'google_drive' | 'as400_export'>('as400');
  const [form, setForm] = useState<QueryFormState>(emptyQueryForm);
  const [mappingTarget, setMappingTarget] = useState<MappingTarget>('clienti');
  const [mappingNodeId, setMappingNodeId] = useState('');
  const [mappingSchemaName, setMappingSchemaName] = useState('MPA5DAT');
  const [mappingTableName, setMappingTableName] = useState('');
  const [mappingColumns, setMappingColumns] = useState<string[]>([]);
  const [mappingFieldMap, setMappingFieldMap] = useState<Record<string, string>>({});
  const [mappingKeyMap, setMappingKeyMap] = useState<Record<string, string>>({});
  const [mappingSourceTsField, setMappingSourceTsField] = useState('');
  const [mappingKeyFilter, setMappingKeyFilter] = useState('');
  const [mappingFieldFilter, setMappingFieldFilter] = useState('');
  const [mappingSqlText, setMappingSqlText] = useState('');
  const [mappingCreateMissingCliente, setMappingCreateMissingCliente] = useState(false);
  const [studioTableFilter, setStudioTableFilter] = useState('');
  const [studioTablePage, setStudioTablePage] = useState(1);
  const [studioTableRows, setStudioTableRows] = useState<Record<string, any>[]>([]);
  const [studioTableHasMore, setStudioTableHasMore] = useState(false);
  const [inspectJobId, setInspectJobId] = useState<string | null>(null);
  const [tableColumnsByName, setTableColumnsByName] = useState<Record<string, TableColumnMeta[]>>({});
  const [inspectRequest, setInspectRequest] = useState<{
    mode: 'list_tables' | 'list_columns';
    purpose: 'browse_tables' | 'browse_columns' | 'autocomplete_tables' | 'autocomplete_columns';
    tableName?: string;
    page?: number;
  } | null>(null);

  const [previewNodeId, setPreviewNodeId] = useState('');
  const [previewParamsJson, setPreviewParamsJson] = useState('{}');
  const [previewPage, setPreviewPage] = useState(1);
  const [previewRowsLimit, setPreviewRowsLimit] = useState(50);
  const [previewJobId, setPreviewJobId] = useState<string | null>(null);
  const [isStudioSaved, setIsStudioSaved] = useState(false);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const autocompleteDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tableHintsRef = useRef<string[]>([]);
  const mappingColumnsRef = useRef<string[]>([]);
  const tableColumnsByNameRef = useRef<Record<string, TableColumnMeta[]>>({});
  const lastAutoTablePrefixRef = useRef<string>('');
  const lastAutoColumnsTableRef = useRef<string>('');

  const tenantsQuery = useQuery({
    queryKey: ['sync-studio', 'tenants'],
    queryFn: async () => (await syncAdminAPI.listTenants({ heartbeat_window_seconds: 180 })).data.data,
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (!selectedTenantId && tenantsQuery.data?.length) {
      setSelectedTenantId(tenantsQuery.data[0].tenant_id);
    }
  }, [selectedTenantId, tenantsQuery.data]);

  const selectedTenant = useMemo(
    () => tenantsQuery.data?.find((t: SyncTenantAccess) => t.tenant_id === selectedTenantId) || null,
    [tenantsQuery.data, selectedTenantId]
  );

  const nodesQuery = useQuery({
    queryKey: ['sync-studio', 'nodes', selectedTenantId],
    queryFn: async () => (await syncAdminAPI.listDaemonNodes(selectedTenantId, { heartbeat_window_seconds: 180 })).data.data,
    enabled: !!selectedTenantId,
    refetchInterval: 30000,
  });

  const queriesQuery = useQuery({
    queryKey: ['sync-studio', 'queries', selectedTenantId],
    queryFn: async () => (await syncAdminAPI.listQueries(selectedTenantId)).data.data,
    enabled: !!selectedTenantId,
  });

  useEffect(() => {
    if (!mappingNodeId) {
      const firstActive = (nodesQuery.data || []).find((n: SyncDaemonNode) => n.is_active)?.node_id || '';
      setMappingNodeId(firstActive);
    }
    if (!previewNodeId) {
      const firstActive = (nodesQuery.data || []).find((n: SyncDaemonNode) => n.is_active)?.node_id || '';
      setPreviewNodeId(firstActive);
    }
  }, [mappingNodeId, previewNodeId, nodesQuery.data]);

  useEffect(() => {
    if (!isEdit || !queryId || !queriesQuery.data) return;
    const found = queriesQuery.data.find((q: SyncQueryDefinition) => {
      const current = String(q.query_id || '');
      return (
        current === queryId ||
        current.toLowerCase() === queryId.toLowerCase() ||
        encodeURIComponent(current) === queryId
      );
    });
    if (!found) return;

    setSourceType(found.source_type || 'as400');
    setForm({
      schedule_window: found.schedule_window || {},
      source_config: found.source_config || {},
      query_id: found.query_id,
      name: found.name,
      description: found.description || '',
      sql_text: found.sql_text,
      params_schema: JSON.stringify(found.params_schema || {}, null, 2),
      business_key_fields: (found.business_key_fields || []).join(', '),
      source_ts_field: found.source_ts_field || '',
      schedule_minutes: found.schedule_minutes,
      is_active: found.is_active,
    });
    setMappingSqlText(found.sql_text || '');
    setMappingSourceTsField(found.source_ts_field || '');

    const mapping = (found.params_schema as any)?.__mapping;
    if (mapping && typeof mapping === 'object') {
      const target = String(mapping.target || '').toLowerCase() as MappingTarget;
      if (target in MAPPING_TARGET_CONFIG) setMappingTarget(target);
      setMappingFieldMap(mapping.fields && typeof mapping.fields === 'object' ? mapping.fields : {});
      setMappingKeyMap(mapping.keys && typeof mapping.keys === 'object' ? mapping.keys : {});
      setMappingCreateMissingCliente(Boolean(mapping?.options?.create_missing_cliente));
      const serializedColumns = Array.isArray(mapping?.selected_columns)
        ? mapping.selected_columns.map((v: unknown) => String(v || '').trim()).filter(Boolean)
        : [];
      if (serializedColumns.length > 0) setMappingColumns(serializedColumns);
      const sourceSchema = String(mapping?.source?.schema || '').trim();
      const sourceTable = String(mapping?.source?.table || '').trim();
      if (sourceSchema) setMappingSchemaName(sourceSchema);
      if (sourceTable) setMappingTableName(sourceTable);
    }

    const parsedSqlSource = parseFromTable(found.sql_text || '');
    if (!mappingTableName && parsedSqlSource.table) setMappingTableName(parsedSqlSource.table);
    if ((!mappingSchemaName || mappingSchemaName === 'MPA5DAT') && parsedSqlSource.schema) setMappingSchemaName(parsedSqlSource.schema);
  }, [isEdit, queryId, queriesQuery.data]);

  const targetConfig = MAPPING_TARGET_CONFIG[mappingTarget];
  const sqlSelectedColumns = useMemo(() => extractSelectColumns(form.sql_text), [form.sql_text]);
  const mappingColumnsWithLabel = useMemo<MappingColumnOption[]>(() => {
    if (sqlSelectedColumns.length > 0) {
      const dedup = new Map<string, MappingColumnOption>();
      for (const item of sqlSelectedColumns) {
        if (!dedup.has(item.value)) dedup.set(item.value, item);
      }
      return Array.from(dedup.values());
    }
    if (mappingTableName) {
      return mappingColumns.map((name) => ({ value: name, label: `${mappingTableName}.${name}` }));
    }
    return mappingColumns.map((name) => ({ value: name, label: name }));
  }, [sqlSelectedColumns, mappingColumns, mappingTableName]);
  const mappingSelectableColumns = useMemo(() => {
    if (sqlSelectedColumns.length > 0) return Array.from(new Set(sqlSelectedColumns.map((c) => c.value)));
    return mappingColumns;
  }, [sqlSelectedColumns, mappingColumns]);
  const filteredKeyColumnOptions = useMemo(() => {
    const term = mappingKeyFilter.trim().toLowerCase();
    if (!term) return mappingColumnsWithLabel;
    return mappingColumnsWithLabel.filter((opt) => opt.value.toLowerCase().includes(term) || opt.label.toLowerCase().includes(term));
  }, [mappingColumnsWithLabel, mappingKeyFilter]);
  const filteredFieldColumnOptions = useMemo(() => {
    const term = mappingFieldFilter.trim().toLowerCase();
    if (!term) return mappingColumnsWithLabel;
    return mappingColumnsWithLabel.filter((opt) => opt.value.toLowerCase().includes(term) || opt.label.toLowerCase().includes(term));
  }, [mappingColumnsWithLabel, mappingFieldFilter]);

  useEffect(() => {
    if (!mappingColumns.length) return;
    setMappingFieldMap((prev) => {
      const next = { ...prev };
      for (const field of targetConfig.fields) {
        if (!next[field]) {
          const guess = findBestColumnMatch(mappingColumns, field);
          if (guess) next[field] = guess;
        }
      }
      return next;
    });
    setMappingKeyMap((prev) => {
      const next = { ...prev };
      for (const key of targetConfig.keyFields) {
        if (!next[key]) {
          const guess = findBestColumnMatch(mappingColumns, key);
          if (guess) next[key] = guess;
        }
      }
      return next;
    });
  }, [mappingColumns, targetConfig.fields, targetConfig.keyFields]);

  const inspectMutation = useMutation({
    mutationFn: async (payload: {
      mode: 'list_tables' | 'list_columns';
      page?: number;
      tableName?: string;
      tableNameLike?: string;
      purpose: 'browse_tables' | 'browse_columns' | 'autocomplete_tables' | 'autocomplete_columns';
    }) => {
      if (!mappingNodeId) throw new Error('Seleziona un daemon');
      if (!mappingSchemaName.trim()) throw new Error('Schema obbligatorio');
      if (payload.mode === 'list_columns' && !(payload.tableName || mappingTableName).trim()) {
        throw new Error('Tabella obbligatoria');
      }
      return syncAdminAPI.dispatchJob(selectedTenantId, {
        node_id: mappingNodeId,
        command_type: payload.mode,
        query_id: '__explorer__',
        schema_name: mappingSchemaName.trim(),
        table_name: payload.mode === 'list_columns' ? (payload.tableName || mappingTableName).trim() : undefined,
        params: payload.mode === 'list_tables' && (payload.tableNameLike || studioTableFilter).trim()
          ? { table_name_like: `%${(payload.tableNameLike || studioTableFilter).trim().toUpperCase()}%` }
          : {},
        page: payload.page || 1,
        page_size: payload.mode === 'list_tables' ? 20 : 500,
        max_pages: 1,
        priority: 0,
      });
    },
    onSuccess: (res, payload) => {
      setInspectRequest(payload);
      setInspectJobId(res.data.job_id);
    },
    onError: (error: any) => {
      toast.error(error?.message || error?.response?.data?.detail || 'Errore metadata');
    },
  });

  const inspectJobQuery = useQuery({
    queryKey: ['sync-studio', 'inspect-job', selectedTenantId, inspectJobId],
    queryFn: async () => (await syncAdminAPI.getJob(selectedTenantId, inspectJobId!)).data,
    enabled: !!selectedTenantId && !!inspectJobId,
    refetchInterval: (query) => {
      const data = query.state.data as SyncDispatchJob | undefined;
      if (!data) return 1000;
      return data.state === 'completed' || data.state === 'failed' ? false : 1000;
    },
  });

  useEffect(() => {
    if (!inspectJobId || !inspectJobQuery.data || !inspectRequest) return;
    if (inspectJobQuery.data.state === 'failed') {
      toast.error(inspectJobQuery.data.error_message || 'Errore metadata');
      setInspectRequest(null);
      setInspectJobId(null);
      return;
    }
    if (inspectJobQuery.data.state !== 'completed') return;

    const progress: any = inspectJobQuery.data.progress || {};
    const records = Array.isArray(progress.records) ? progress.records : [];

    if (inspectRequest.mode === 'list_tables') {
      if (inspectRequest.purpose === 'browse_tables') {
        setStudioTableRows(records);
        setStudioTableHasMore(Boolean(progress.has_more));
        setStudioTablePage(Number(progress.page || studioTablePage));
      } else {
        const fetchedHints = records
          .map((row: Record<string, any>) => `${String(row.TABLE_SCHEMA || '').trim()}.${String(row.TABLE_NAME || '').trim()}`)
          .filter((v: string) => v !== '.');
        const merged = Array.from(new Set([...tableHintsRef.current, ...fetchedHints]));
        tableHintsRef.current = merged;
      }
    } else {
      const extractedMetaMap = new Map<string, TableColumnMeta>();
      for (const r of records) {
        const name = String(r.COLUMN_NAME || r.column_name || '').trim();
        if (!name) continue;
        const descriptionRaw = String(
          r.COLUMN_TEXT ||
          r.column_text ||
          r.COLUMN_DESCRIPTION ||
          r.column_description ||
          r.REMARKS ||
          r.remarks ||
          r.DESCRIPTION ||
          r.description ||
          ''
        ).trim();
        extractedMetaMap.set(name, { name, description: descriptionRaw || undefined });
      }
      const extractedMeta = Array.from(extractedMetaMap.values());
      const extracted = extractedMeta.map((c) => c.name);
      const effectiveTable = String(inspectRequest.tableName || mappingTableName || '').trim();
      if (effectiveTable) {
        setTableColumnsByName((prev) => ({ ...prev, [effectiveTable]: extractedMeta }));
      }
      if (inspectRequest.purpose === 'browse_columns') {
        setMappingColumns(extracted);
        toast.success(`Colonne caricate: ${extracted.length}`);
      }
    }
    setInspectRequest(null);
    setInspectJobId(null);
  }, [inspectJobId, inspectJobQuery.data, inspectRequest, studioTablePage, mappingTableName]);

  useEffect(() => {
    tableHintsRef.current = Array.from(new Set(
      studioTableRows
        .map((row) => `${String(row.TABLE_SCHEMA || '').trim()}.${String(row.TABLE_NAME || '').trim()}`)
        .filter((full) => full !== '.')
    ));
  }, [studioTableRows]);

  useEffect(() => {
    mappingColumnsRef.current = mappingColumns;
  }, [mappingColumns]);

  useEffect(() => {
    tableColumnsByNameRef.current = tableColumnsByName;
  }, [tableColumnsByName]);

  useEffect(() => {
    if (!mappingTableName) return;
    const cached = tableColumnsByName[mappingTableName];
    if (cached && cached.length) setMappingColumns(cached.map((c) => c.name));
  }, [mappingTableName, tableColumnsByName]);

  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!previewNodeId) throw new Error('Seleziona un daemon preview');
      if (!form.sql_text.trim()) throw new Error('SQL vuota');
      let parsedParams: Record<string, any> = {};
      try {
        parsedParams = previewParamsJson.trim() ? JSON.parse(previewParamsJson) : {};
      } catch {
        throw new Error('Parametri preview non validi');
      }
      const effectiveSql = withRowsOnlyClause(form.sql_text, previewRowsLimit);
      if (!effectiveSql.trim()) throw new Error('SQL vuota');
      return syncAdminAPI.dispatchJob(selectedTenantId, {
        node_id: previewNodeId,
        command_type: 'query_preview',
        query_id: '__explorer__',
        sql_text: effectiveSql,
        params: parsedParams,
        page: previewPage,
        page_size: clampRowsLimit(previewRowsLimit),
        max_pages: 1,
        priority: 0,
      });
    },
    onSuccess: (res) => setPreviewJobId(res.data.job_id),
    onError: (error: any) => toast.error(error?.message || error?.response?.data?.detail || 'Errore preview'),
  });

  const previewJobQuery = useQuery({
    queryKey: ['sync-studio', 'preview-job', selectedTenantId, previewJobId],
    queryFn: async () => (await syncAdminAPI.getJob(selectedTenantId, previewJobId!)).data,
    enabled: !!selectedTenantId && !!previewJobId,
    refetchInterval: (query) => {
      const data = query.state.data as SyncDispatchJob | undefined;
      if (!data) return 1000;
      return data.state === 'completed' || data.state === 'failed' ? false : 1000;
    },
  });

  const generateSqlFromMapping = () => {
    const schema = mappingSchemaName.trim();
    const table = mappingTableName.trim();
    if (!schema || !table) return toast.error('Imposta schema e tabella');
    const selectedColumns = Array.from(new Set([
      ...targetConfig.keyFields.map((k) => mappingKeyMap[k]).filter(Boolean),
      ...targetConfig.fields.map((f) => mappingFieldMap[f]).filter(Boolean),
    ]));
    const sql = selectedColumns.length
      ? `SELECT\n  ${selectedColumns.join(',\n  ')}\nFROM ${schema}.${table}`
      : `SELECT *\nFROM ${schema}.${table}`;
    setMappingSqlText(sql);
    setForm((prev) => ({ ...prev, sql_text: sql }));
  };

  const applyMapping = () => {
    const fields = Object.fromEntries(
      targetConfig.fields.map((f) => [f, (mappingFieldMap[f] || '').trim()] as const).filter(([, v]) => !!v)
    );
    const keys = Object.fromEntries(
      targetConfig.keyFields.map((k) => [k, (mappingKeyMap[k] || '').trim()] as const).filter(([, v]) => !!v)
    );
    if (Object.keys(fields).length === 0) return toast.error('Mappa almeno un campo');
    const mappingPayload: any = {
      target: mappingTarget,
      keys,
      fields,
      selected_columns: mappingSelectableColumns,
      source: {
        schema: mappingSchemaName.trim() || undefined,
        table: mappingTableName.trim() || undefined,
      },
    };
    if (mappingTarget === 'offerte' && mappingCreateMissingCliente) mappingPayload.options = { create_missing_cliente: true };
    const businessKeys = Array.from(new Set(Object.values(keys).filter(Boolean)));
    const fallbackQueryId = `${targetConfig.defaultQueryId}_${normalizeIdentifier(mappingTableName || 'source')}`;
    setForm((prev) => ({
      ...prev,
      query_id: prev.query_id.trim() || fallbackQueryId,
      name: prev.name.trim() || `${targetConfig.defaultName} ${mappingTableName ? `(${mappingTableName})` : ''}`.trim(),
      sql_text: mappingSqlText.trim() || prev.sql_text,
      params_schema: JSON.stringify({ __mapping: mappingPayload }, null, 2),
      business_key_fields: businessKeys.join(', '),
      source_ts_field: mappingSourceTsField.trim(),
    }));
    toast.success('Mapping applicato');
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      setIsStudioSaved(false);
      let parsedParams: Record<string, any> = {};
      try {
        parsedParams = form.params_schema.trim() ? JSON.parse(form.params_schema) : {};
      } catch {
        parsedParams = {};
      }
      const businessKeys = form.business_key_fields.split(',').map((x) => x.trim()).filter(Boolean);
      if (!businessKeys.length) throw new Error('business_key_fields obbligatorio');
      const fields = Object.fromEntries(
        targetConfig.fields.map((f) => [f, (mappingFieldMap[f] || '').trim()] as const).filter(([, v]) => !!v)
      );
      const keys = Object.fromEntries(
        targetConfig.keyFields.map((k) => [k, (mappingKeyMap[k] || '').trim()] as const).filter(([, v]) => !!v)
      );
      const mappingPayload: any = {
        target: mappingTarget,
        keys,
        fields,
        selected_columns: mappingSelectableColumns,
        source: {
          schema: mappingSchemaName.trim() || undefined,
          table: mappingTableName.trim() || undefined,
        },
      };
      if (mappingTarget === 'offerte' && mappingCreateMissingCliente) mappingPayload.options = { create_missing_cliente: true };
      parsedParams.__mapping = mappingPayload;

      if (isEdit && queryId) {
        const response = await syncAdminAPI.updateQuery(selectedTenantId, queryId, {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          sql_text: form.sql_text,
          params_schema: parsedParams,
          business_key_fields: businessKeys,
          source_ts_field: form.source_ts_field.trim() || undefined,
          schedule_minutes: form.schedule_minutes,
          schedule_window: form.schedule_window,
          source_config: form.source_config,
          is_active: form.is_active,
        });
        return { mode: 'edit' as const, queryId: queryId, response };
      }
      const response = await syncAdminAPI.createQuery(selectedTenantId, {
        query_id: form.query_id.trim(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        sql_text: form.sql_text,
        params_schema: parsedParams,
        business_key_fields: businessKeys,
        source_ts_field: form.source_ts_field.trim() || undefined,
        schedule_minutes: form.schedule_minutes,
        schedule_window: form.schedule_window,
        source_config: form.source_config,
        is_active: form.is_active,
      });
      return { mode: 'create' as const, queryId: response.data.query_id, response };
    },
    onSuccess: (result) => {
      toast.success(result.mode === 'edit' ? 'Query aggiornata' : 'Query creata');
      setIsStudioSaved(true);
      queryClient.invalidateQueries({ queryKey: ['sync-admin', 'queries', selectedTenantId] });
      queryClient.invalidateQueries({ queryKey: ['sync-studio', 'queries', selectedTenantId] });
      if (result.mode === 'create' && result.queryId) {
        navigate(`/sync-admin/query/${encodeURIComponent(result.queryId)}/edit?tab=${encodeURIComponent(returnTab)}&tenant=${encodeURIComponent(selectedTenantId)}`, { replace: true });
      }
    },
    onError: (error: any) => {
      toast.error(error?.message || error?.response?.data?.detail || 'Errore salvataggio query');
    },
  });

  const previewColumns = ((previewJobQuery.data?.progress as any)?.columns as string[] | undefined) || [];
  const previewRows = ((previewJobQuery.data?.progress as any)?.records as Record<string, any>[] | undefined) || [];
  const previewRowsCount = Number((previewJobQuery.data?.progress as any)?.rows_total ?? previewRows.length ?? 0);
  const isLoadingTables = inspectMutation.isPending || (inspectJobId !== null && inspectRequest?.mode === 'list_tables');
  const isLoadingColumns = inspectMutation.isPending || (inspectJobId !== null && inspectRequest?.mode === 'list_columns');
  const isPreviewRunning =
    previewMutation.isPending ||
    (!!previewJobId && !!previewJobQuery.data && previewJobQuery.data.state !== 'completed' && previewJobQuery.data.state !== 'failed');
  const selectedTableColumns = tableColumnsByName[mappingTableName] || [];

  const requestTableColumns = (tableName: string, purpose: 'browse_columns' | 'autocomplete_columns') => {
    const normalized = String(tableName || '').trim();
    if (!normalized) return;
    if (purpose === 'browse_columns' && tableColumnsByNameRef.current[normalized]) {
      setMappingColumns(tableColumnsByNameRef.current[normalized].map((c) => c.name));
      return;
    }
    if (inspectMutation.isPending || inspectJobId) return;
    inspectMutation.mutate({ mode: 'list_columns', tableName: normalized, page: 1, purpose });
  };

  const remapAgainstAvailableColumns = (availableColumns: string[]) => {
    const available = new Set(availableColumns.map((c) => c.toLowerCase()));
    setMappingKeyMap((prev) => {
      const next: Record<string, string> = {};
      for (const keyName of targetConfig.keyFields) {
        const current = String(prev[keyName] || '').trim();
        if (current && available.has(current.toLowerCase())) {
          next[keyName] = current;
          continue;
        }
        const guessed = findBestColumnMatch(availableColumns, keyName);
        next[keyName] = guessed || '';
      }
      return next;
    });
    setMappingFieldMap((prev) => {
      const next: Record<string, string> = {};
      for (const fieldName of targetConfig.fields) {
        const current = String(prev[fieldName] || '').trim();
        if (current && available.has(current.toLowerCase())) {
          next[fieldName] = current;
          continue;
        }
        const guessed = findBestColumnMatch(availableColumns, fieldName);
        next[fieldName] = guessed || '';
      }
      return next;
    });
  };

  const regenerateColumnsFromSql = () => {
    const fromSqlColumns = extractSelectColumns(form.sql_text);
    if (fromSqlColumns.length > 0) {
      const uniq = Array.from(new Set(fromSqlColumns.map((c) => c.value)));
      setMappingColumns(uniq);
      remapAgainstAvailableColumns(uniq);
      toast.success(`Colonne rigenerate da SQL: ${uniq.length}`);
      return;
    }
    const source = parseFromTable(form.sql_text);
    if (source.table) {
      if (source.schema) setMappingSchemaName(source.schema);
      setMappingTableName(source.table);
      requestTableColumns(source.table, 'browse_columns');
      toast.success('Richiesta colonne inviata dalla tabella nel FROM');
      return;
    }
    toast.error('Nessuna colonna o tabella riconosciuta nello SQL');
  };

  useEffect(() => {
    if (!mappingTableName || !mappingNodeId || !selectedTenantId) return;
    if (tableColumnsByNameRef.current[mappingTableName]?.length) {
      const cols = tableColumnsByNameRef.current[mappingTableName].map((c) => c.name);
      setMappingColumns(cols);
      remapAgainstAvailableColumns(cols);
      return;
    }
    requestTableColumns(mappingTableName, 'browse_columns');
  }, [mappingTableName, mappingNodeId, selectedTenantId]);

  useEffect(() => {
    if (!mappingSelectableColumns.length) return;
    remapAgainstAvailableColumns(mappingSelectableColumns);
  }, [mappingSelectableColumns, mappingTarget]);

  const handleSqlEditorChange = (value: string | undefined) => {
    const nextSql = value || '';
    setForm((p) => ({ ...p, sql_text: nextSql }));
    if (autocompleteDebounceRef.current) clearTimeout(autocompleteDebounceRef.current);
    autocompleteDebounceRef.current = setTimeout(() => {
      const editor = editorRef.current;
      if (!editor || !mappingNodeId || !selectedTenantId || inspectMutation.isPending || inspectJobId) return;
      const model = editor.getModel();
      const position = editor.getPosition();
      if (!model || !position) return;
      const linePrefix = model.getLineContent(position.lineNumber).slice(0, Math.max(position.column - 1, 0));

      const tableTokenMatch = linePrefix.match(/(?:from|join)\s+([a-zA-Z0-9_]*)(?:\.([a-zA-Z0-9_]*))?$/i);
      const tablePrefix = tableTokenMatch ? (tableTokenMatch[2] || tableTokenMatch[1] || '').trim() : '';
      if (tablePrefix.length >= 2 && tablePrefix.toLowerCase() !== lastAutoTablePrefixRef.current.toLowerCase()) {
        lastAutoTablePrefixRef.current = tablePrefix;
        inspectMutation.mutate({ mode: 'list_tables', page: 1, purpose: 'autocomplete_tables', tableNameLike: tablePrefix });
      }

      const qualifierMatch = linePrefix.match(/([a-zA-Z0-9_]+)\.\w*$/);
      const qualifier = qualifierMatch?.[1] || '';
      if (!qualifier) return;
      const lowered = qualifier.toLowerCase();
      const fullTableHints = Array.from(new Set([...tableHintsRef.current, ...Object.keys(tableColumnsByNameRef.current)]));
      const matched = fullTableHints.find((t) => {
        const tableOnly = t.split('.').pop() || t;
        return tableOnly.toLowerCase() === lowered || tableOnly.toLowerCase().startsWith(lowered);
      });
      const targetTable = matched ? (matched.split('.').pop() || matched) : qualifier;
      if (targetTable.length >= 2 && targetTable.toLowerCase() !== lastAutoColumnsTableRef.current.toLowerCase()) {
        lastAutoColumnsTableRef.current = targetTable;
        requestTableColumns(targetTable, 'autocomplete_columns');
      }
      editor.trigger('keyboard', 'editor.action.triggerSuggest', {});
    }, 250);
  };

  if (!canManageSync) {
    return <div className="p-6 text-red-700">Accesso negato</div>;
  }

  const sourcePicker = <label className="flex flex-col gap-1 text-sm">Sorgente<select aria-label="Sorgente processo" className="rounded border p-2" disabled={isEdit} value={sourceType} onChange={e => setSourceType(e.target.value as 'as400' | 'google_drive' | 'as400_export')}><option value="as400">AS400 (demone)</option><option value="google_drive">Google Drive (server)</option><option value="as400_export">Invio dati → AS400</option></select></label>;
  if (isEdit && queriesQuery.isPending) return <p className="p-6">Caricamento processo…</p>;
  if (sourceType === 'google_drive' || sourceType === 'as400_export') return <div className="space-y-4 p-6">
    <div className="flex items-center justify-between"><h1 className="text-2xl font-bold">{isEdit ? 'Modifica processo' : 'Nuovo processo'}</h1><button className="rounded border p-2" onClick={() => navigate(`/sync-admin?tab=query-catalog&tenant=${selectedTenantId}`)}>Torna ai processi</button></div>
    <div className="flex flex-wrap gap-3"><label className="flex flex-col text-sm">Tenant<select aria-label="Tenant processo" disabled={isEdit} className="rounded border p-2" value={selectedTenantId} onChange={e => setSelectedTenantId(e.target.value)}>{tenantsQuery.data?.map(tenant => <option key={tenant.tenant_id} value={tenant.tenant_id}>{tenant.tenant_name}</option>)}</select></label>{sourcePicker}</div>
    {selectedTenantId && sourceType === 'as400_export' && <MepaProcessEditor key={`${selectedTenantId}-${queryId || 'new'}`} tenantId={selectedTenantId} process={queriesQuery.data?.find(process => process.query_id === queryId)} />}
    {selectedTenantId && sourceType === 'google_drive' && <DriveProcessEditor key={`${selectedTenantId}-${queryId || 'new'}`} tenantId={selectedTenantId} process={queriesQuery.data?.find(process => process.query_id === queryId)} />}
  </div>;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{isEdit ? `Modifica processo (${queryId})` : 'Nuovo processo'}</h1>
        <div className="flex items-center gap-2">
          {saveMutation.isPending && <span className="text-xs font-medium text-indigo-700">Salvataggio in corso...</span>}
          {!saveMutation.isPending && isStudioSaved && <span className="text-xs font-medium text-green-700">Salvato</span>}
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saveMutation.isPending ? 'Salvataggio...' : isEdit ? 'Salva modifiche' : 'Crea processo'}
          </button>
          <button
            onClick={() => navigate(`/sync-admin?tab=${encodeURIComponent(returnTab)}&tenant=${encodeURIComponent(selectedTenantId)}`)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Torna a Sync Admin
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        {sourcePicker}
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
          <select
            value={selectedTenantId}
            disabled={isEdit}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            {(tenantsQuery.data || []).map((t: SyncTenantAccess) => (
              <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</option>
            ))}
          </select>
          <input
            value={form.query_id}
            onChange={(e) => setForm((p) => ({ ...p, query_id: e.target.value }))}
            disabled={isEdit}
            placeholder="query_id"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="Nome processo"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-3 xl:col-span-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-900">Ricerca Tabelle & Metadata</p>
          <select
            value={mappingNodeId}
            onChange={(e) => setMappingNodeId(e.target.value)}
            className="w-full rounded-md border border-blue-200 bg-white px-3 py-2 text-sm"
          >
            <option value="">Seleziona daemon</option>
            {(nodesQuery.data || []).filter((n: SyncDaemonNode) => n.is_active).map((n: SyncDaemonNode) => (
              <option key={n.node_id} value={n.node_id}>{n.node_id}</option>
            ))}
          </select>
          <input
            value={mappingSchemaName}
            onChange={(e) => setMappingSchemaName(e.target.value)}
            placeholder="Schema"
            className="w-full rounded-md border border-blue-200 bg-white px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <input
              value={studioTableFilter}
              onChange={(e) => setStudioTableFilter(e.target.value)}
              placeholder="Filtro tabella"
              className="w-full rounded-md border border-blue-200 bg-white px-3 py-2 text-sm"
            />
            <button
              onClick={() => inspectMutation.mutate({ mode: 'list_tables', page: 1, purpose: 'browse_tables' })}
              disabled={isLoadingTables}
              className="rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoadingTables ? 'Caricamento...' : 'Cerca'}
            </button>
          </div>
          {isLoadingTables && (
            <div className="rounded border border-blue-200 bg-white px-2 py-1 text-[11px] text-blue-800">
              Ricerca tabelle in esecuzione...
            </div>
          )}
          <div className="max-h-64 overflow-auto rounded border border-blue-200 bg-white">
            {studioTableRows.map((row, idx) => {
              const schema = String(row.TABLE_SCHEMA || '').trim();
              const table = String(row.TABLE_NAME || '').trim();
              return (
                <button
                  key={`${schema}.${table}.${idx}`}
                  onClick={() => {
                    setMappingTableName(table);
                    requestTableColumns(table, 'browse_columns');
                  }}
                  className="block w-full border-b border-blue-100 px-2 py-2 text-left hover:bg-blue-50"
                >
                  <div className="font-mono text-xs text-blue-900">{schema}.{table}</div>
                  <div className="text-[11px] text-gray-600">{String(row.TABLE_TEXT || '').trim() || '-'}</div>
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between text-[11px] text-gray-600">
            <span>Pagina {studioTablePage}</span>
            <div className="flex gap-1">
              <button
                onClick={() => inspectMutation.mutate({ mode: 'list_tables', page: Math.max(1, studioTablePage - 1), purpose: 'browse_tables' })}
                disabled={studioTablePage <= 1 || !selectedTenant || isLoadingTables}
                className="rounded border border-blue-200 bg-white px-2 py-1 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => inspectMutation.mutate({ mode: 'list_tables', page: studioTablePage + 1, purpose: 'browse_tables' })}
                disabled={!studioTableHasMore || !selectedTenant || isLoadingTables}
                className="rounded border border-blue-200 bg-white px-2 py-1 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
          <div className="rounded border border-blue-200 bg-white p-2">
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="text-[11px] font-semibold text-blue-900">
                Colonne tabella {mappingTableName ? mappingTableName : '(nessuna)'}
              </p>
              <button
                onClick={() => {
                  if (!mappingTableName) return toast.error('Seleziona prima una tabella');
                  requestTableColumns(mappingTableName, 'browse_columns');
                }}
                disabled={isLoadingColumns || !mappingTableName}
                className="rounded border border-blue-200 bg-white px-2 py-1 text-[10px] text-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoadingColumns ? 'Caricamento...' : 'Carica colonne'}
              </button>
            </div>
            {isLoadingColumns ? (
              <p className="text-[11px] text-blue-800">Caricamento colonne...</p>
            ) : selectedTableColumns.length > 0 ? (
              <div className="max-h-40 space-y-1 overflow-auto">
                {selectedTableColumns.map((col) => (
                  <div key={`selected-col-${col.name}`} className="rounded border border-blue-200 bg-blue-50 px-2 py-1">
                    <div className="font-mono text-[10px] text-blue-900">{col.name}</div>
                    <div className="text-[10px] text-gray-600">{col.description || '-'}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-gray-500">Seleziona una tabella per vedere le colonne.</p>
            )}
          </div>
        </div>

        <div className="space-y-3 xl:col-span-2">
          <div className="rounded-md border border-gray-200">
            <Editor
              height="300px"
              defaultLanguage="sql"
              value={form.sql_text}
              onChange={handleSqlEditorChange}
              onMount={(editor, monaco) => {
                editorRef.current = editor;
                monaco.languages.registerCompletionItemProvider('sql', {
                  provideCompletionItems(model: Monaco.editor.ITextModel, position: Monaco.Position) {
                    const word = model.getWordUntilPosition(position);
                    const prefix = String(word.word || '').toLowerCase();
                    const linePrefix = model.getLineContent(position.lineNumber).slice(0, Math.max(position.column - 1, 0));
                    const range = {
                      startLineNumber: position.lineNumber,
                      endLineNumber: position.lineNumber,
                      startColumn: word.startColumn,
                      endColumn: word.endColumn,
                    };
                    const allTableHints = Array.from(new Set([
                      ...tableHintsRef.current,
                      ...Object.keys(tableColumnsByNameRef.current),
                    ]));
                    const qualifierMatch = linePrefix.match(/([a-zA-Z0-9_]+)\.\w*$/);
                    const qualifier = qualifierMatch?.[1]?.toLowerCase() || '';
                    let scopedColumns: string[] = [];
                    if (qualifier) {
                      const matchedTable = allTableHints.find((t) => {
                        const tableOnly = t.split('.').pop() || t;
                        return tableOnly.toLowerCase() === qualifier || tableOnly.toLowerCase().startsWith(qualifier);
                      });
                      const key = matchedTable ? (matchedTable.split('.').pop() || matchedTable) : qualifier;
                      scopedColumns = (tableColumnsByNameRef.current[key] || []).map((c) => c.name);
                    }
                    const genericColumns = Array.from(new Set([
                      ...mappingColumnsRef.current,
                      ...Object.values(tableColumnsByNameRef.current).flat().map((c) => c.name),
                    ]));
                    const candidateColumns = scopedColumns.length > 0 ? scopedColumns : genericColumns;
                    const columnSuggestions = candidateColumns
                      .filter((col) => !prefix || col.toLowerCase().startsWith(prefix))
                      .map((col) => ({
                      label: col,
                      kind: monaco.languages.CompletionItemKind.Field,
                      insertText: col,
                      range,
                    }));
                    const tableSuggestions = allTableHints
                      .filter((table) => {
                        const tableOnly = table.split('.').pop() || table;
                        return !prefix || tableOnly.toLowerCase().startsWith(prefix) || table.toLowerCase().startsWith(prefix);
                      })
                      .map((table) => ({
                      label: table,
                      kind: monaco.languages.CompletionItemKind.Struct,
                      insertText: table,
                      range,
                    }));
                    const keywordSuggestions = SQL_KEYWORDS
                      .filter((kw) => !prefix || kw.toLowerCase().startsWith(prefix))
                      .map((kw) => ({
                      label: kw,
                      kind: monaco.languages.CompletionItemKind.Keyword,
                      insertText: kw,
                      range,
                    }));
                    return {
                      suggestions: [...columnSuggestions, ...tableSuggestions, ...keywordSuggestions],
                    };
                  },
                });

                editor.updateOptions({
                  quickSuggestions: { other: true, comments: false, strings: false },
                  suggestOnTriggerCharacters: true,
                  wordBasedSuggestions: 'allDocuments',
                } as Monaco.editor.IStandaloneEditorConstructionOptions);
              }}
              options={{ minimap: { enabled: false }, fontSize: 13, wordWrap: 'on', automaticLayout: true }}
            />
          </div>
          <div className="flex items-center justify-end">
            <button
              onClick={() => previewMutation.mutate()}
              disabled={isPreviewRunning}
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isPreviewRunning ? 'Esecuzione...' : 'Esegui query'}
            </button>
          </div>

          <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-900">Mappatura Entità</p>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
              <select
                value={mappingTarget}
                onChange={(e) => {
                  const next = e.target.value as MappingTarget;
                  setMappingTarget(next);
                  setMappingFieldMap({});
                  setMappingKeyMap({});
                }}
                className="rounded-md border border-blue-200 bg-white px-3 py-2 text-sm"
              >
                {Object.entries(MAPPING_TARGET_CONFIG).map(([value, cfg]) => (
                  <option key={value} value={value}>{cfg.label}</option>
                ))}
              </select>
              <input
                value={mappingTableName}
                onChange={(e) => setMappingTableName(e.target.value)}
                placeholder="Tabella selezionata"
                className="rounded-md border border-blue-200 bg-white px-3 py-2 text-sm"
              />
              <input
                value={mappingSourceTsField}
                onChange={(e) => setMappingSourceTsField(e.target.value)}
                placeholder="source_ts_field (opz.)"
                className="rounded-md border border-blue-200 bg-white px-3 py-2 text-sm"
              />
              <div className="flex gap-2">
                <button
                  onClick={generateSqlFromMapping}
                  className="rounded-md border border-blue-300 bg-white px-2.5 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100"
                >
                  SQL
                </button>
                <button
                  onClick={regenerateColumnsFromSql}
                  className="rounded-md border border-blue-300 bg-white px-2.5 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100"
                >
                  Rigenera colonne
                </button>
                <button
                  onClick={applyMapping}
                  className="rounded-md bg-blue-700 px-2.5 py-2 text-xs font-semibold text-white hover:bg-blue-800"
                >
                  Applica
                </button>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
              <div className="space-y-2">
                <input
                  value={mappingKeyFilter}
                  onChange={(e) => setMappingKeyFilter(e.target.value)}
                  placeholder="Filtra colonne chiave..."
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs"
                />
                {targetConfig.keyFields.map((keyName) => (
                  <div key={keyName} className="grid grid-cols-2 gap-2">
                    <div className="rounded border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700">{keyName}</div>
                    <select
                      value={mappingKeyMap[keyName] || ''}
                      onChange={(e) => setMappingKeyMap((prev) => ({ ...prev, [keyName]: e.target.value }))}
                      className="rounded border border-gray-300 px-2 py-1.5 text-xs"
                    >
                      <option value="">-- colonna --</option>
                      {filteredKeyColumnOptions.map((col) => (
                        <option key={`k-${keyName}-${col.value}`} value={col.value}>{col.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              <div className="max-h-48 space-y-2 overflow-auto pr-1">
                <input
                  value={mappingFieldFilter}
                  onChange={(e) => setMappingFieldFilter(e.target.value)}
                  placeholder="Filtra colonne output..."
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs"
                />
                {targetConfig.fields.map((fieldName) => (
                  <div key={fieldName} className="grid grid-cols-2 gap-2">
                    <div className="rounded border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700">{fieldName}</div>
                    <select
                      value={mappingFieldMap[fieldName] || ''}
                      onChange={(e) => setMappingFieldMap((prev) => ({ ...prev, [fieldName]: e.target.value }))}
                      className="rounded border border-gray-300 px-2 py-1.5 text-xs"
                    >
                      <option value="">-- colonna --</option>
                      {filteredFieldColumnOptions.map((col) => (
                        <option key={`f-${fieldName}-${col.value}`} value={col.value}>{col.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-900">Preview Query</p>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
              <select
                value={previewNodeId}
                onChange={(e) => setPreviewNodeId(e.target.value)}
                className="rounded-md border border-blue-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">Seleziona daemon</option>
                {(nodesQuery.data || []).filter((n: SyncDaemonNode) => n.is_active).map((n: SyncDaemonNode) => (
                  <option key={n.node_id} value={n.node_id}>{n.node_id}</option>
                ))}
              </select>
              <input type="number" min={1} value={previewPage} onChange={(e) => setPreviewPage(Number(e.target.value))} className="rounded-md border border-blue-200 bg-white px-3 py-2 text-sm" />
              <input type="number" min={1} max={100} value={previewRowsLimit} onChange={(e) => setPreviewRowsLimit(clampRowsLimit(Number(e.target.value)))} className="rounded-md border border-blue-200 bg-white px-3 py-2 text-sm" />
              <button
                onClick={() => previewMutation.mutate()}
                disabled={isPreviewRunning}
                className="rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isPreviewRunning ? 'Esecuzione...' : 'Esegui preview'}
              </button>
            </div>
            <div className="mt-2 text-xs text-blue-900">
              Righe estratte: <span className="font-semibold">{previewRowsCount}</span> (min 1, max 100)
            </div>
            <textarea
              value={previewParamsJson}
              onChange={(e) => setPreviewParamsJson(e.target.value)}
              placeholder='Parametri preview JSON'
              className="mt-2 w-full rounded-md border border-blue-200 bg-white px-3 py-2 font-mono text-xs"
              rows={2}
            />
            {previewColumns.length > 0 && (
              <div className="mt-2 max-h-52 overflow-auto rounded border border-blue-200 bg-white">
                <table className="min-w-full divide-y divide-blue-100 text-xs">
                  <thead className="bg-blue-50">
                    <tr>{previewColumns.map((col) => <th key={col} className="px-2 py-1.5 text-left">{col}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-blue-50">
                    {previewRows.map((row, idx) => (
                      <tr key={idx}>{previewColumns.map((col) => <td key={`${idx}-${col}`} className="px-2 py-1">{String(row[col] ?? '')}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <ProcessSchedule minutes={form.schedule_minutes} window={form.schedule_window} active={form.is_active} onChange={value => setForm(previous => ({ ...previous, schedule_minutes: value.minutes, schedule_window: value.window, is_active: value.active }))} />
      <label className="flex flex-col text-sm">Demone di esecuzione<select className="rounded border p-2" value={form.source_config.node_id || ''} onChange={e => setForm(previous => ({ ...previous, source_config: { node_id: e.target.value } }))}><option value="">Tutti i demoni attivi del tenant</option>{nodesQuery.data?.filter(node => node.is_active).map(node => <option key={node.node_id} value={node.node_id}>{node.node_id}</option>)}</select></label>
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <input value={form.business_key_fields} onChange={(e) => setForm((p) => ({ ...p, business_key_fields: e.target.value }))} placeholder="business_key_fields" className="rounded-md border border-gray-300 px-3 py-2 text-sm" />
          <input value={form.source_ts_field} onChange={(e) => setForm((p) => ({ ...p, source_ts_field: e.target.value }))} placeholder="source_ts_field" className="rounded-md border border-gray-300 px-3 py-2 text-sm" />
          <label className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} />
            Processo attivo
          </label>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={() => navigate(`/sync-admin?tab=${encodeURIComponent(returnTab)}&tenant=${encodeURIComponent(selectedTenantId)}`)} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Annulla
          </button>
        </div>
      </div>

      {selectedTenant && (
        <div className="text-xs text-gray-500">
          Tenant: {selectedTenant.tenant_name}
        </div>
      )}
    </div>
  );
}
